import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ServiceOfferingShopCategory } from '@freelancer/datastore/collections';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import {
  InputEditInlineType,
  InputSize,
  InputType,
} from '@freelancer/ui/input';
import { LinkIconPosition, LinkUnderline } from '@freelancer/ui/link';
import { FontWeight } from '@freelancer/ui/text';
import { dirtyAndValidate } from '@freelancer/ui/validators';
import type { FormGroupTypeOf } from '@freelancer/utils';
import type {
  CarouselHeaderFormType,
  CategorySaveEvent,
} from './service-offering-carousel.types';

@Component({
  standalone: false,
  selector: 'fl-service-offering-carousel-header-display',
  template: `
    <!--
      DESKTOP:
        - category name and description are side by side
        - category name and description are both part of the link
        - no chevron icon at the end of the link
    -->
    <fl-link
      class="mobile:hide"
      flTrackingLabel="ServiceOfferingCategoryLink"
      i18n="Service offering carousel category name and description"
      [link]="category.seoUrl"
      [underline]="LinkUnderline.NEVER"
    >
      <div class="CategoryCarouselHeader">
        <h2 class="text-primary text-body-24 desktop:text-title-32 mr-xxsmall">
          {{ category.name }}.
        </h2>
        <span class="text-foreground text-body-16 desktop:text-title-32">
          {{ category.description }}
        </span>
      </div>
    </fl-link>
    <!--
      MOBILE:
        - category name and description are stacked vertically
        - only the category name is part of the link
        - has chevron icon at the end of the link
    -->
    <div class="mobile:show">
      <fl-link
        flTrackingLabel="ServiceOfferingCategoryLink"
        i18n="Service offering carousel category name"
        [iconAnimation]="true"
        [iconColor]="IconColor.PRIMARY"
        [iconLabel]="'View ' + category.name + ' services'"
        [iconName]="'ui-arrow-forward'"
        [iconPosition]="LinkIconPosition.RIGHT"
        [iconSize]="IconSize.SMALL"
        [link]="category.seoUrl"
        [underline]="LinkUnderline.NEVER"
      >
        <h2 class="text-primary text-body-24 desktop:text-title-32 mr-xxsmall">
          {{ category.name }}
        </h2>
      </fl-link>
      <p class="text-foreground text-body-16 desktop:text-title-32">
        {{ category.description }}
      </p>
    </div>
  `,
  styleUrls: ['./service-offering-carousel-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceOfferingCarouselHeaderDisplayComponent {
  IconColor = IconColor;
  IconSize = IconSize;
  LinkIconPosition = LinkIconPosition;
  LinkUnderline = LinkUnderline;

  @Input() category: ServiceOfferingShopCategory;
}

@Component({
  standalone: false,
  selector: 'fl-service-offering-carousel-header-edit',
  template: `
    <div
      class="CategoryCarouselHeader mb-xxsmall mr-small desktop:mr-none desktop:hide"
      [class.mobile:hide]="false"
      [class.tablet:hide]="false"
    >
      <fl-input
        [control]="form.controls.name"
        [editInline]="InputEditInlineType.BORDERED_BOTTOM"
        [type]="InputType.TEXT"
        [fontWeight]="FontWeight.BOLD"
        [placeholder]="namePlaceholder"
        [size]="InputSize.MID"
        [editInlineTitleSize]="'large'"
      >
      </fl-input>
      <fl-input
        class="mb-xsmall"
        [control]="form.controls.description"
        [editInline]="InputEditInlineType.BORDERED_BOTTOM"
        [type]="InputType.TEXT"
        [placeholder]="descriptionPlaceholder"
        [size]="InputSize.SMALL"
      >
      </fl-input>
      <div class="ActionContainer">
        <fl-button
          i18n="Cancel button"
          flTrackingLabel="CancelButton"
          class="ActionButton"
          [color]="ButtonColor.TEXT_FOREGROUND"
          [size]="ButtonSize.SMALL"
          (click)="handleCancel()"
        >
          Cancel
        </fl-button>
        <fl-button
          i18n="Save button"
          flTrackingLabel="SaveButton"
          class="ActionButton"
          [color]="ButtonColor.PRIMARY"
          [size]="ButtonSize.SMALL"
          (click)="handleSave()"
        >
          Save
        </fl-button>
      </div>
    </div>
    <div
      class="CategoryCarouselHeader mb-small mobile:hide tablet:hide"
      [class.desktop:hide]="false"
    >
      <fl-input
        class="CategoryCarouselHeader-desktopFormName mr-xxsmall"
        [control]="form.controls.name"
        [editInline]="InputEditInlineType.BORDERED_BOTTOM"
        [type]="InputType.TEXT"
        [fontWeight]="FontWeight.BOLD"
        [placeholder]="namePlaceholder"
        [size]="InputSize.LARGE"
        [editInlineTitleSize]="'xxlarge'"
      >
      </fl-input>
      <fl-input
        class="CategoryCarouselHeader-desktopFormDescription desktop:mr-xsmall"
        [control]="form.controls.description"
        [editInline]="InputEditInlineType.BORDERED_BOTTOM"
        [type]="InputType.TEXT"
        [placeholder]="descriptionPlaceholder"
        [size]="InputSize.LARGE"
        [editInlineTitleSize]="'xxlarge'"
      >
      </fl-input>
      <div class="ActionContainer">
        <fl-button
          i18n="Cancel button"
          flTrackingLabel="CancelButton"
          class="ActionButton"
          [color]="ButtonColor.TEXT_FOREGROUND"
          [size]="ButtonSize.SMALL"
          (click)="handleCancel()"
        >
          <p
            class="text-color-inherit text-body-20 ActionButton CancelText underline"
          >
            Cancel
          </p>
        </fl-button>
        <fl-button
          i18n="Save button"
          flTrackingLabel="SaveButton"
          class="ActionButton"
          [color]="ButtonColor.PRIMARY"
          [sizeDesktop]="ButtonSize.LARGE"
          (click)="handleSave()"
        >
          Save
        </fl-button>
      </div>
    </div>
  `,
  styleUrls: ['./service-offering-carousel-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceOfferingCarouselHeaderEditComponent {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FontWeight = FontWeight;
  InputEditInlineType = InputEditInlineType;
  InputSize = InputSize;
  InputType = InputType;

  @Input() category?: ServiceOfferingShopCategory;
  @Input() form: FormGroup<FormGroupTypeOf<CarouselHeaderFormType>>;
  @Input() namePlaceholder: string;
  @Input() descriptionPlaceholder: string;

  @Output() save = new EventEmitter<CategorySaveEvent>();
  @Output() cancel = new EventEmitter<
    ServiceOfferingShopCategory | undefined
  >();

  handleCancel(): void {
    this.form.setValue({
      name: this.category?.name ?? '',
      description: this.category?.description ?? '',
    });
    this.cancel.emit(this.category);
  }

  handleSave(): void {
    dirtyAndValidate(this.form);
    if (this.form.valid) {
      this.save.emit({
        category: this.category,
        updates: {
          name: this.form.controls.name.value,
          description: this.form.controls.description.value,
        },
      });
    }
  }
}

@Component({
  standalone: false,
  selector: 'fl-service-offering-carousel-header',
  template: `
    @if (isLoading) {
      <fl-loading-text
        class="LoadingText"
        [rows]="1"
      ></fl-loading-text>
    }

    @if (!isEditMode && category && !isLoading) {
      <fl-service-offering-carousel-header-display [category]="category">
      </fl-service-offering-carousel-header-display>
    }
    @if (isEditMode && form && !isLoading) {
      <fl-service-offering-carousel-header-edit
        [category]="category"
        [form]="form"
        [namePlaceholder]="namePlaceholder"
        [descriptionPlaceholder]="descriptionPlaceholder"
        (save)="save.emit($event)"
        (cancel)="cancel.emit($event)"
      >
      </fl-service-offering-carousel-header-edit>
    }
  `,
  styleUrls: ['./service-offering-carousel-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceOfferingCarouselHeaderComponent {
  @Input() isEditMode = false;
  @Input() isLoading = false;
  @Input() category?: ServiceOfferingShopCategory;
  @Input() form?: FormGroup<FormGroupTypeOf<CarouselHeaderFormType>>;
  @Input() namePlaceholder: string;
  @Input() descriptionPlaceholder: string;

  @Output() cancel = new EventEmitter<
    ServiceOfferingShopCategory | undefined
  >();
  @Output() save = new EventEmitter<CategorySaveEvent>();
}
